/**
 * Agent Hub — Visual agent dashboard with category grouping and Internal/External views.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Bot,
  Search,
  RefreshCw,
  Plus,
  Zap,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Activity,
  ArrowRight,
  Network,
  Sparkles,
  Loader2,
  Blocks,
  ExternalLink,
  Building2,
  Globe2,
  Link2,
} from 'lucide-react';
import { cpFetch } from '@/lib/cpClient';
import type { AgentInfo } from '@/lib/agentClient';
import { useAppStore } from '@/store/appStore';
import {
  CATEGORIES,
  getDomainVisual,
  getAgentIcon,
  getCategoryForDomain,
  type CategoryDef,
} from '../agentVisuals';
import {
  agentHealthLabel,
  agentStageLabel,
  countHonestActive,
  countWorkflowSteps,
  isHonestProduction,
  isHonestlyHealthy,
  isLeftoverCoreAgent,
  isProbeOrSmokeAgent,
  isTerminalStubAgent,
  measureFailed,
} from '../agentHonesty';
import {
  buildIntertwineGraph,
  emptyIntertwine,
  type AgentIntertwine,
} from '../agentIntertwine';

type DeploymentStage = 'all' | 'draft' | 'staging' | 'production';
type Scope = 'all' | 'internal' | 'external';

interface AgentExt extends AgentInfo {
  agent_id?: string;
  volcano_config?: any;
  leftover?: boolean;
  probe_or_smoke?: boolean;
  terminal_stub?: boolean;
  honestly_active?: boolean;
  honestly_healthy?: boolean;
  honesty_stage?: string;
  step_count?: number;
  health_status?: { is_healthy: boolean; last_check: string };
  updated_at?: string;
  created_at?: string;
  depends_on?: string[];
  feeds_into?: string[];
  connectors?: Array<{ type?: string; agent_id?: string; agentId?: string; name?: string }>
    | Record<string, { type?: string; agent_id?: string; agentId?: string; name?: string } | null>
    | null;
  relationships?: { depends_on?: string[]; feeds_into?: string[]; data_flow?: any[] };
}

type HubHonesty = {
  listed?: number;
  active?: number;
  leftovers?: number;
  leftover_names?: string[];
  stubs?: number;
  healthy?: number;
  probes?: number;
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const stageColors: Record<string, string> = {
  production: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  staging: 'bg-amber-100 text-amber-700 border-amber-200',
  draft: 'bg-gray-100 text-gray-600 border-gray-200',
};

const stageIcons: Record<string, React.ReactNode> = {
  production: <CheckCircle2 className="w-3 h-3" />,
  staging: <AlertTriangle className="w-3 h-3" />,
  draft: <Clock className="w-3 h-3" />,
};

function isVolcano(a: AgentExt) {
  return (
    a.execution_engine === 'volcano' ||
    !!a.volcano_workflow ||
    !!(a.volcano_config?.workflows)
  );
}

function dedup(agents: AgentExt[]): AgentExt[] {
  const map = new Map<string, AgentExt>();
  for (const a of agents) {
    const key = a.agent_id || a.id;
    const existing = map.get(key);
    if (!existing) { map.set(key, a); continue; }
    const nv = parseFloat(a.version || '0');
    const ev = parseFloat(existing.version || '0');
    if (nv > ev || (a.updated_at && existing.updated_at && a.updated_at > existing.updated_at)) {
      map.set(key, a);
    }
  }
  return Array.from(map.values());
}

function isExternal(a: AgentExt): boolean {
  return a.agent_type === 'app' || a.agent_type === 'external';
}

/* ------------------------------------------------------------------ */
/*  Category Section                                                   */
/* ------------------------------------------------------------------ */

function CategorySection({
  category,
  agents,
  navigate,
  agentNameMap,
  intertwineByAgent,
}: {
  category: CategoryDef;
  agents: AgentExt[];
  navigate: (path: string) => void;
  agentNameMap: Map<string, string>;
  intertwineByAgent: Map<string, AgentIntertwine>;
}) {
  const CatIcon = category.icon;

  // Color classes based on category.color
  const colorMap: Record<string, { header: string; iconBg: string; iconText: string; line: string }> = {
    indigo:  { header: 'text-indigo-800', iconBg: 'bg-indigo-100', iconText: 'text-indigo-600', line: 'bg-indigo-200' },
    blue:    { header: 'text-blue-800',   iconBg: 'bg-blue-100',   iconText: 'text-blue-600',   line: 'bg-blue-200' },
    emerald: { header: 'text-emerald-800', iconBg: 'bg-emerald-100', iconText: 'text-emerald-600', line: 'bg-emerald-200' },
    cyan:    { header: 'text-cyan-800',   iconBg: 'bg-cyan-100',   iconText: 'text-cyan-600',   line: 'bg-cyan-200' },
    slate:   { header: 'text-slate-800',  iconBg: 'bg-slate-100',  iconText: 'text-slate-600',  line: 'bg-slate-200' },
    gray:    { header: 'text-gray-700',   iconBg: 'bg-gray-100',   iconText: 'text-gray-500',   line: 'bg-gray-200' },
  };

  const colors = colorMap[category.color] ?? colorMap.gray;

  return (
    <div className="mb-8">
      {/* Category header */}
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-8 h-8 rounded-lg ${colors.iconBg} flex items-center justify-center`}>
          <CatIcon className={`w-4.5 h-4.5 ${colors.iconText}`} />
        </div>
        <div className="flex items-center gap-3 flex-1">
          <h2 className={`text-sm font-bold uppercase tracking-wide ${colors.header}`}>
            {category.label}
          </h2>
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-medium">
            {agents.length} {agents.length === 1 ? 'agent' : 'agents'}
          </Badge>
          <div className={`flex-1 h-px ${colors.line}`} />
        </div>
      </div>

      {/* Agent cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {agents.map((agent) => {
          const id = agent.agent_id || agent.id;
          return (
            <AgentCard
              key={id}
              agent={agent}
              navigate={navigate}
              agentNameMap={agentNameMap}
              intertwine={intertwineByAgent.get(id) || emptyIntertwine(id)}
            />
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Agent Card                                                         */
/* ------------------------------------------------------------------ */

function AgentCard({
  agent,
  navigate,
  agentNameMap,
  intertwine,
}: {
  agent: AgentExt;
  navigate: (path: string) => void;
  agentNameMap: Map<string, string>;
  intertwine: AgentIntertwine;
}) {
  const id = agent.agent_id || agent.id;
  const volcano = isVolcano(agent);
  const external = isExternal(agent);
  const vis = getDomainVisual(agent.domain);
  const AgentIcon = getAgentIcon(agent);
  const healthy = isHonestlyHealthy(agent);
  const healthLabel = agentHealthLabel(agent);
  const stageLabel = agentStageLabel(agent);
  const probe = isProbeOrSmokeAgent(agent);
  const leftover = isLeftoverCoreAgent(agent);
  const stub = isTerminalStubAgent(agent);
  const connectedIds = intertwine.downstreamAgents;
  const connectedTools = intertwine.downstreamTools;
  const connectionCount = intertwine.connectionCount;

  const stepCount = countWorkflowSteps(agent);

  return (
    <Card
      className="bg-white hover:shadow-lg transition-all duration-200 cursor-pointer group overflow-hidden border border-gray-200 hover:border-gray-300"
      onClick={() => navigate(`/agents/hub/${id}`)}
    >
      {/* Colored top stripe */}
      <div className={`h-1.5 bg-gradient-to-r ${vis.stripe}`} />

      <CardContent className="p-5">
        {/* Header: large icon + big name */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-12 h-12 rounded-xl ${vis.bgLight} flex items-center justify-center flex-shrink-0 shadow-sm`}>
              <AgentIcon className={`w-6 h-6 ${vis.textColor}`} />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-gray-900 truncate leading-tight">{agent.name}</h3>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-violet-500 transition-colors flex-shrink-0" />
        </div>

        {/* Badges row */}
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          {/* Deployment stage */}
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${leftover || probe || stub ? 'bg-amber-50 text-amber-800 border-amber-200' : (stageColors[agent.deployment_stage] || stageColors.draft)}`}>
            {leftover || probe || stub ? <AlertTriangle className="w-2.5 h-2.5" /> : (stageIcons[agent.deployment_stage] || stageIcons.draft)}
            <span className="ml-1">{stageLabel}</span>
          </Badge>

          {/* Volcano engine badge */}
          {volcano && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">
              <Zap className="w-2.5 h-2.5 mr-0.5" />
              {`${stepCount} steps`}
            </Badge>
          )}

          {/* External badge */}
          {external && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-sky-50 text-sky-700 border-sky-200">
              <ExternalLink className="w-2.5 h-2.5 mr-0.5" />
              External
            </Badge>
          )}
        </div>

        {/* Invoke / MCP connections from volcano steps */}
        {connectionCount > 0 && (
          <div className="mb-3">
            <div className="flex items-center gap-1 mb-1.5">
              <Link2 className="w-3 h-3 text-gray-400" />
              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                {connectionCount} connection{connectionCount === 1 ? '' : 's'}
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {connectedIds.slice(0, 3).map((cid) => (
                <span
                  key={cid}
                  className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200 hover:bg-violet-50 hover:text-violet-700 hover:border-violet-200 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/agents/hub/${cid}`);
                  }}
                >
                  <Bot className="w-2.5 h-2.5" />
                  {agentNameMap.get(cid) || cid}
                </span>
              ))}
              {connectedTools.slice(0, 3).map((tool) => (
                <span
                  key={tool.id}
                  className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
                >
                  {tool.tool_name}
                </span>
              ))}
              {connectionCount > connectedIds.slice(0, 3).length + connectedTools.slice(0, 3).length && (
                <span className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full bg-gray-50 text-gray-400">
                  +{connectionCount - connectedIds.slice(0, 3).length - connectedTools.slice(0, 3).length} more
                </span>
              )}
            </div>
          </div>
        )}

        {connectionCount === 0 && (
          <div className="flex items-center gap-1.5 mb-3 text-[10px] text-gray-300">
            <Link2 className="w-3 h-3" />
            <span>No connections</span>
          </div>
        )}

        {/* Schedule / last-run (UI truth F5) */}
        {(() => {
          const schedules = Array.isArray((agent as any).schedules) ? (agent as any).schedules : [];
          const lastRun = (agent as any).last_run_at || null;
          if (!schedules.length && !lastRun) return null;
          const cronBits = schedules
            .map((s: any) => s.cron_desc || s.cron)
            .filter(Boolean)
            .slice(0, 3);
          return (
            <div className="mb-3 rounded-md border border-violet-100 bg-violet-50/50 px-2.5 py-2 space-y-1">
              {cronBits.length > 0 && (
                <div className="text-[10px] text-violet-900">
                  <span className="font-medium uppercase tracking-wide text-violet-600 mr-1">Schedule</span>
                  <span className="font-mono">{cronBits.join(' · ')}</span>
                </div>
              )}
              {lastRun && (
                <div className="text-[10px] text-violet-800">
                  <span className="font-medium uppercase tracking-wide text-violet-600 mr-1">Last run</span>
                  <span className="font-mono">{new Date(lastRun).toLocaleString()}</span>
                </div>
              )}
            </div>
          );
        })()}

        {/* Footer: health status + version */}
        <div className="flex items-center justify-between text-[11px] text-gray-400 pt-2 border-t border-gray-100">
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${healthy ? 'bg-emerald-400' : probe || healthLabel === 'Health unknown' ? 'bg-amber-400' : 'bg-red-400'}`} />
            <span>{healthLabel}</span>
          </div>
          {agent.version && (
            <span className="font-mono">v{agent.version}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export default function AgentHub() {
  const navigate = useNavigate();
  const location = useLocation();
  const { appId, appName } = useAppStore();
  const [agents, setAgents] = useState<AgentExt[]>([]);
  const [hubHonesty, setHubHonesty] = useState<HubHonesty | null>(null);
  const [failedMeasure, setFailedMeasure] = useState<{ measured: boolean; failed: number | null; total: number }>({
    measured: false,
    failed: null,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState<DeploymentStage>('all');
  const [scope, setScope] = useState<Scope>('all');
  const [currentAppOnly, setCurrentAppOnly] = useState(false);

  const activeTab = useMemo(() => {
    if (location.pathname.startsWith('/agents/create')) return 'create';
    if (location.pathname.startsWith('/agents/observe')) return 'observe';
    if (location.pathname.startsWith('/agents/ecosystem')) return 'ecosystem';
    if (location.pathname.startsWith('/agents/steps')) return 'steps';
    return 'hub';
  }, [location.pathname]);

  useEffect(() => { loadAgents(); }, []);

  const loadAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await cpFetch('/api/agents/discover');
      const list = Array.isArray(res?.agents) ? (res.agents as AgentExt[]) : [];
      setAgents(dedup(list));
      setHubHonesty(res?.hub_honesty && typeof res.hub_honesty === 'object' ? res.hub_honesty : null);
      try {
        const execRes = await cpFetch('/api/agents/executions?limit=50');
        if (execRes?.failed_measure && typeof execRes.failed_measure.measured === 'boolean') {
          setFailedMeasure(execRes.failed_measure);
        } else {
          setFailedMeasure(measureFailed(execRes?.executions || execRes?.history || []));
        }
      } catch {
        setFailedMeasure({ measured: false, failed: null, total: 0 });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Derived data ---------- */

  const filtered = useMemo(() => {
    return agents.filter((a) => {
      if (currentAppOnly) {
        if (!appId) return false;
        if (String(a.app_id || '') !== String(appId)) return false;
      }
      if (stage !== 'all' && a.deployment_stage !== stage) return false;
      if (scope === 'internal' && isExternal(a)) return false;
      if (scope === 'external' && !isExternal(a)) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${a.id} ${a.name} ${a.description} ${a.domain} ${(a.capabilities || []).join(' ')}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [agents, stage, scope, search]);

  /** Group filtered agents by category, sorted by category order */
  const groupedByCategory = useMemo(() => {
    const map = new Map<string, { category: CategoryDef; agents: AgentExt[] }>();
    for (const a of filtered) {
      const cat = getCategoryForDomain(a.domain);
      const existing = map.get(cat.id);
      if (existing) {
        existing.agents.push(a);
      } else {
        map.set(cat.id, { category: cat, agents: [a] });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.category.order - b.category.order);
  }, [filtered]);

  /** Map agent id → display name for connection pills */
  const agentNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of agents) {
      const key = a.agent_id || a.id;
      m.set(key, a.name || key);
    }
    return m;
  }, [agents]);

  const intertwineByAgent = useMemo(() => buildIntertwineGraph(agents).byAgent, [agents]);

  const stats = useMemo(() => {
    const leftovers = typeof hubHonesty?.leftovers === 'number'
      ? hubHonesty.leftovers
      : agents.filter(isLeftoverCoreAgent).length;
    const stubs = typeof hubHonesty?.stubs === 'number'
      ? hubHonesty.stubs
      : agents.filter(isTerminalStubAgent).length;
    const total = typeof hubHonesty?.active === 'number'
      ? hubHonesty.active
      : countHonestActive(agents);
    const internal = agents.filter((a) => !isExternal(a) && !isLeftoverCoreAgent(a)).length;
    const external = agents.filter((a) => isExternal(a) && !isLeftoverCoreAgent(a)).length;
    const prod = agents.filter(isHonestProduction).length;
    const volcano = agents.filter(isVolcano).length;
    const healthy = typeof hubHonesty?.healthy === 'number'
      ? hubHonesty.healthy
      : agents.filter(isHonestlyHealthy).length;
    return { total, leftovers, stubs, internal, external, prod, volcano, healthy };
  }, [agents, hubHonesty]);

  /* ---------- Render ---------- */

  return (
    <div className="h-full flex flex-col">
      {/* ── Top Navigation Bar ────────────────────────────────────── */}
      <div className="border-b bg-white px-6 pt-4 pb-0">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-violet-600 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Agent Manager</h1>
              <p className="text-xs text-gray-500">Build, run, and observe your agentic workflows</p>
            </div>
          </div>
          <Button
            size="sm"
            className="bg-violet-600 hover:bg-violet-700"
            onClick={() => navigate('/agents/create')}
          >
            <Plus className="w-4 h-4 mr-1.5" />
            New Agent
          </Button>
        </div>

        <nav className="flex gap-1 -mb-px">
          {[
            { id: 'hub', label: 'Agents', icon: Bot, path: '/agents/hub' },
            { id: 'create', label: 'Create', icon: Sparkles, path: '/agents/create' },
            { id: 'observe', label: 'Observe', icon: Activity, path: '/agents/observe' },
            { id: 'ecosystem', label: 'Ecosystem', icon: Network, path: '/agents/ecosystem' },
            { id: 'steps', label: 'Steps', icon: Blocks, path: '/agents/steps' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              className={`
                flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors
                ${activeTab === tab.id
                  ? 'border-violet-600 text-violet-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Content ───────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto bg-gray-50 p-6">

        {/* ── Quick Stats ──────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 mb-6">
          {[
            { label: 'Active (excl. draft/probe)', value: stats.total, icon: Bot, color: 'text-violet-600', bg: 'bg-violet-50' },
            { label: 'Internal',    value: stats.internal, icon: Building2,    color: 'text-blue-600',    bg: 'bg-blue-50' },
            { label: 'External',    value: stats.external, icon: Globe2,       color: 'text-sky-600',     bg: 'bg-sky-50' },
            { label: 'Production (excl. probes)',  value: stats.prod,     icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { label: 'Healthy (explicit)',     value: stats.healthy,  icon: Activity,     color: 'text-green-600',   bg: 'bg-green-50' },
            { label: failedMeasure.measured ? 'Failed (measured)' : 'Failed (unmeasured)', value: failedMeasure.measured ? failedMeasure.failed : '—', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
          ].map((s) => (
            <Card key={s.label} className={`${s.bg} border-0 shadow-sm`}>
              <CardContent className="p-3.5 flex items-center gap-3">
                <s.icon className={`w-7 h-7 ${s.color} opacity-80`} />
                <div>
                  <p className="text-lg font-bold text-gray-900 leading-none">{s.value}</p>
                  <p className="text-[10px] text-gray-500 font-medium mt-0.5">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="text-[11px] text-gray-500 -mt-4 mb-6">
          Active excludes drafts, probes, smoke, leftover app names, and terminal stubs. Missing health is unknown, not healthy — not the header 25–27/27 service flap.
          Failed is unmeasured until execution history exists — Failed=0 with no measurement is not shown.
          {stats.leftovers > 0 ? ` ${stats.leftovers} leftover name(s) are still in the live list and are not counted as Active.` : ''}
          {stats.stubs > 0 ? ` ${stats.stubs} terminal stub(s) stay listed and are not counted as Active or Production.` : ''}
        </p>

        {/* ── Scope Toggle + Filters ───────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {/* Internal / External toggle */}
          <div className="flex rounded-lg border bg-white p-0.5 shadow-sm">
            {([
              { id: 'all' as Scope,      label: 'All',      icon: Bot },
              { id: 'internal' as Scope,  label: 'Internal', icon: Building2 },
              { id: 'external' as Scope,  label: 'External', icon: Globe2 },
            ]).map((s) => (
              <button
                key={s.id}
                onClick={() => setScope(s.id)}
                className={`
                  flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-xs font-medium transition-all
                  ${scope === s.id
                    ? 'bg-violet-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50'
                  }
                `}
              >
                <s.icon className="w-3.5 h-3.5" />
                {s.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search agents by name, domain, or capability..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-white"
            />
          </div>

          {/* Stage filter */}
          <select
            value={stage}
            onChange={(e) => setStage(e.target.value as DeploymentStage)}
            className="text-sm border rounded-md px-3 py-2 bg-white text-gray-700"
          >
            <option value="all">All Stages</option>
            <option value="draft">Draft</option>
            <option value="staging">Staging</option>
            <option value="production">Production</option>
          </select>

          {/* Current App Filter */}
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 bg-white border rounded-md px-3 py-2">
            <input
              type="checkbox"
              checked={currentAppOnly}
              onChange={(e) => setCurrentAppOnly(e.target.checked)}
              className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
            />
            <span>
              Current app only
              {appId ? (
                <span className="ml-1 text-xs text-gray-500">({appName || appId})</span>
              ) : (
                <span className="ml-1 text-xs text-amber-600">(no app selected)</span>
              )}
            </span>
          </label>

          {/* Refresh */}
          <Button variant="ghost" size="sm" onClick={loadAgents} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* ── Error ────────────────────────────────────────────────── */}
        {error && (
          <div className="bg-red-50 text-red-700 text-sm rounded-lg p-3 mb-4">{error}</div>
        )}

        {/* ── Loading ──────────────────────────────────────────────── */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400 mr-2" />
            <span className="text-gray-500">Loading agents...</span>
          </div>
        )}

        {/* ── Agent Grid — Grouped by Category ─────────────────────── */}
        {!loading && (
          <>
            {filtered.length === 0 && (
              <div className="text-center py-20">
                <Bot className="w-14 h-14 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 font-semibold">No agents found</p>
                <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or create a new agent.</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => navigate('/agents/create')}
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  Create Agent
                </Button>
              </div>
            )}

            {groupedByCategory.map(({ category, agents: catAgents }) => (
              <CategorySection
                key={category.id}
                category={category}
                agents={catAgents}
                navigate={navigate}
                agentNameMap={agentNameMap}
                intertwineByAgent={intertwineByAgent}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}
